import dayjs from "dayjs";
import {
  DateValue,
  CurrencyValue,
  DateSelectionType,
  CurrencySelectionType,
  CompanyIdentificationValue,
  CompanyIdentificationType,
  BaseCurrencyValue,
  BaseCurrencySelectionType,
  QuoteCurrencyValue,
  QuoteCurrencySelectionType,
  TIndexLookUpApproach,
  TradedFundsIdentification
} from "../types/newLinkBuilder";

export enum FormulaType {
  Equity = 'equity',
  Currency = 'currency',
  Company = 'company',
  TradedFunds = 'tradedFunds',
  CapitalizationActuals = 'capitalizationActuals',
  CustomBuiltIndex = 'customBuiltIndex',
  DerivedMarketData = 'derivedMarketData',
}

export async function buildEquityFormula(
  params: {
    selectedDataPoint: string | null,
    companyIdentification?: CompanyIdentificationValue;
    date: DateValue;
    currency?: CurrencyValue;
  }
): Promise<string> {
  const isVolumeAndTrendingCurrencySelected = ['Traded_Volume_Shares', 'Trading_Currency'].includes(params.selectedDataPoint || "");
  const isTradingDays = params.selectedDataPoint === 'Trading_Days';
  const isOtherMarketData = isVolumeAndTrendingCurrencySelected || isTradingDays;
  let formula = '=Alpha.Share_Price_Data(';
  if (isOtherMarketData) {
    formula = '=Alpha.Other_Market_Data(';
  }

  const { companyIdentification, date, currency } = params;
  if (companyIdentification?.selected === CompanyIdentificationType.Search) {
    formula += `"${companyIdentification.searchInput || "N/A"}", `;
  } else {
    formula += `${companyIdentification?.exchange || "N/A"}&":"&${companyIdentification?.ticker || "N/A"}, `;
  }

  formula += `"${params.selectedDataPoint || "N/A"}", `;

  if (isTradingDays) {
    if (date.selected === DateSelectionType.Date) {
      formula += `"${date.beginDate ? dayjs(date.beginDate).format("MM/DD/YYYY") : "N/A"}", `;
      formula += `"${date.endDate ? dayjs(date.endDate).format("MM/DD/YYYY") : "N/A"}"`;
    } else {
      formula += `${date.beginDateCellReference || "N/A"}, `;
      formula += `${date.endDateCellReference || "N/A"}`;
    }
  } else {
    const trailingComma = isVolumeAndTrendingCurrencySelected ? "" : ", ";
    if (date.selected === DateSelectionType.Date) {
      formula += `"${date.date ? dayjs(date.date).format("MM/DD/YYYY") : "N/A"}"${trailingComma}`;
    } else {
      formula += `${date.dateCellReference || "N/A"}${trailingComma}`;
    }
  }

  if (!isOtherMarketData) {
    if (currency?.selected === CurrencySelectionType.Value) {
      formula += `"${currency.currency || "N/A"}"`;
    } else {
      formula += `${currency.currencyCellReference || "N/A"}`;
    }
  }

  formula += `)`;
  return formula;
}

export async function buildCurrencyFormula(
  params: {
    selectedDataPoint: string | null,
    date: DateValue;
    baseCurrency?: BaseCurrencyValue;
    quoteCurrency?: QuoteCurrencyValue;
  }
): Promise<string> {
  let formula = '=Alpha.Currency(';
  const { baseCurrency, date, quoteCurrency } = params;

  /**
   * Scenario	Cell Value(s)               Desired String	Formula
    1. Both parts in cells                L1 = "USD" L2 = "GBP"	USD:GBP	=A.x(L1 & ":" & L2)
    2. Prefix is literal, symbol in cell	L2 = "GBP"	USD:GBP	=A.x("USD:" & L2)
    3. Prefix in cell, symbol is literal	L1 = "USD"	USD:GBP	=A.x(L1 & ":GBP")
    4. Both parts literal	                None	USD:GBP	=A.x("USD:GBP")
  */

  if (baseCurrency?.selected === BaseCurrencySelectionType.Value) {
    if (quoteCurrency?.selected === QuoteCurrencySelectionType.Value) {
      formula += `"${baseCurrency.currency || "N/A"}:${quoteCurrency.currency || "N/A"}", `;
    } else {
      formula += `"${baseCurrency.currency || "N/A"}:"&${quoteCurrency?.currencyCellReference || "N/A"}, `;
    }
  } else {
    if (quoteCurrency?.selected === QuoteCurrencySelectionType.Value) {
      formula += `${baseCurrency?.currencyCellReference || "N/A"}&":${quoteCurrency.currency || "N/A"}", `;
    } else {
      formula += `${baseCurrency?.currencyCellReference || "N/A"}&":"&${quoteCurrency?.currencyCellReference || "N/A"}, `;
    }
  }

  formula += `"${params.selectedDataPoint || "N/A"}", `;

  if (date.selected === DateSelectionType.Date) {
    formula += `"${date.date ? dayjs(date.date).format("MM/DD/YYYY") : "N/A"}"`;
  } else {
    formula += `${date.dateCellReference || "N/A"}`;
  }
  formula += `)`;
  return formula;
}

export async function buildCompanyFormula(
  params: {
    selectedDataPoint: string | null,
    companyIdentification: CompanyIdentificationValue,
    currency: CurrencyValue,
    isCurrencyUsed?: boolean,
  }
): Promise<string> {
  let formula = '=Alpha.Company_Data(';
  const { companyIdentification } = params;

  if (companyIdentification?.selected === CompanyIdentificationType.Search) {
    formula += `"${companyIdentification.searchInput || "N/A"}", `;
  } else {
    formula += `${companyIdentification?.exchange || "N/A"}&":"&${companyIdentification?.ticker || "N/A"}, `;
  }

  formula += `"${params.selectedDataPoint || "N/A"}"`;

    // Currency
    if (params.isCurrencyUsed) {
      if (params.currency?.selected === CurrencySelectionType.Value) {
        formula += `, "${params.currency.currency || "N/A"}"`;
      } else {
        formula += `, ${params.currency.currencyCellReference || "N/A"}`;
      }
    }

  formula += `)`;
  return formula;
}

export async function buildTradedFundsFormula(
  params: {
    selectedDataPoint: string | null,
    indexIdentification: TradedFundsIdentification;
    date: DateValue;
    currency: CurrencyValue;
  }
): Promise<string> {
  const isVolumeAndTrendingCurrencySelected = ['Traded_Volume_Shares', 'Trading_Currency'].includes(params.selectedDataPoint || "");
  let formula = '=Alpha.Share_Price_Data(';
  if (isVolumeAndTrendingCurrencySelected) {
    formula = '=Alpha.Other_Market_Data(';
  }

  const { indexIdentification, date, currency } = params;
  if (indexIdentification.selected === TIndexLookUpApproach.CellReference) {
    formula += `${indexIdentification.exchange || "N/A"}&":"&${indexIdentification.ticker || "N/A"}, `;
  } else {
    formula += `"${indexIdentification.index || "N/A"}", `;
  }
  

  formula += `"${params.selectedDataPoint || "N/A"}", `;

  const trailingComma = isVolumeAndTrendingCurrencySelected ? "" : ", ";
  if (date.selected === DateSelectionType.Date) {
    formula += `"${date.date ? dayjs(date.date).format("MM/DD/YYYY") : "N/A"}"${trailingComma}`;
  } else {
    formula += `${date.dateCellReference || "N/A"}${trailingComma}`;
  }

  if (!isVolumeAndTrendingCurrencySelected) {
    if (currency?.selected === CurrencySelectionType.Value) {
      formula += `"${currency.currency || "N/A"}"`;
    } else {
      formula += `${currency.currencyCellReference || "N/A"}`;
    }
  }

  formula += `)`;
  return formula;
} 

export async function buildCapitalizationActualsFormula(
  params: {
    companyIdentification: CompanyIdentificationValue;
    dataType: string;
    dataSource: string;
    date: DateValue;
    dataScale: string;
    isScaleUsed: boolean;
    isCurrencyUsed: boolean;
    currency: CurrencyValue;
  }
): Promise<string> {
  let formula = '=Alpha.Capitalization_Actuals(';
  const { companyIdentification, dataType, dataSource, date, dataScale, isScaleUsed, isCurrencyUsed, currency } = params;

  if (companyIdentification?.selected === CompanyIdentificationType.Search) {
    formula += `"${companyIdentification.searchInput || "N/A"}", `;
  } else {
    formula += `${companyIdentification?.exchange || "N/A"}&":"&${companyIdentification?.ticker || "N/A"}, `;
  }

  formula += `"${dataSource || "N/A"}", `;

  formula += `"${dataType || "N/A"}", `;

  formula += `"${date ? dayjs(date.date).format("MM/DD/YYYY") : "N/A"}"`;

  if (isCurrencyUsed) {
    if (currency?.selected === CurrencySelectionType.Value) {
    formula += `, "${currency.currency}"`;
    }
  }

  if (isScaleUsed) {
    formula += `, "${dataScale}"`;
  }
  
  formula += `)`;
  
  return formula;
}

export async function buildCustomBuiltIndexFormula(
  params: {
    inputRange: string;
    date: DateValue;
  }
): Promise<string> {
  let formula = '=Alpha.Custom_Built_Index(';
  const { inputRange, date } = params;

  // Add input range (can be a cell reference or range)
  if (inputRange) {
    formula += `${inputRange}, `;
  } else {
    formula += `"Input Range", `;
  }

  formula += `"Closing_Index_Value", `;
  // Add date parameter
  if (date.selected === DateSelectionType.Date) {
    formula += `"${date.date ? dayjs(date.date).format("MM/DD/YYYY") : "N/A"}"`;
  } else {
    formula += date.dateCellReference ? `${date.dateCellReference}` : `"N/A"`;
  }

  formula += `)`;
  return formula;
}


export async function buildDerivedMarketDataFormula(
  params: {
    selectedDataPoint: string | null;
    companyIdentification: CompanyIdentificationValue;
    date: DateValue;
    currency: CurrencyValue;
  }
): Promise<string> {
  let formula = '=Alpha.Market_Derived_Data(';
  const { companyIdentification, date, currency, selectedDataPoint } = params;

  // Symbol
  if (companyIdentification?.selected === CompanyIdentificationType.Search) {
    formula += `"${companyIdentification.searchInput || 'N/A'}", `;
  } else {
    formula += `${companyIdentification?.exchange || 'N/A'}&":"&${companyIdentification?.ticker || 'N/A'}, `;
  }

  // Data Type (Period_*)
  formula += `"${selectedDataPoint || 'N/A'}", `;

  // Start / End dates
  if (date.selected === DateSelectionType.Date) {
    formula += `"${date.beginDate ? dayjs(date.beginDate).format('MM/DD/YYYY') : 'N/A'}", `;
    formula += `"${date.endDate ? dayjs(date.endDate).format('MM/DD/YYYY') : 'N/A'}", `;
  } else {
    formula += `${date.beginDateCellReference || 'N/A'}, `;
    formula += `${date.endDateCellReference || 'N/A'}, `;
  }

  // Currency
  if (currency?.selected === CurrencySelectionType.Value) {
    formula += `"${currency.currency || "N/A"}"`;
  } else {
    formula += `${currency.currencyCellReference || "N/A"}`;
  }

  formula += `)`;
  return formula;
}


export async function buildFormula(
  formulaType: FormulaType,
  params: {
    selectedDataPoint?: string | null,
    companyIdentification?: CompanyIdentificationValue;
    indexIdentification?: TradedFundsIdentification;
    date?: DateValue;
    currency?: CurrencyValue;
    baseCurrency?: BaseCurrencyValue;
    quoteCurrency?: QuoteCurrencyValue;
    dataType?: string;
    dataSource?: string;
    dataScale?: string;
    isScaleUsed?: boolean;
    isCurrencyUsed?: boolean;
    inputRange?: string;
  }
): Promise<string> {
  if (formulaType === FormulaType.Equity) {
    return buildEquityFormula({
      selectedDataPoint: params.selectedDataPoint,
      companyIdentification: params.companyIdentification,
      date: params.date,
      currency: params.currency
    });
  } else if (formulaType === FormulaType.Currency) {
    return buildCurrencyFormula({
      selectedDataPoint: params.selectedDataPoint,
      date: params.date,
      baseCurrency: params.baseCurrency,
      quoteCurrency: params.quoteCurrency
    });
  } else if (formulaType === FormulaType.Company) {
    return buildCompanyFormula({
      selectedDataPoint: params.selectedDataPoint,
      companyIdentification: params.companyIdentification,
      currency: params.currency,
      isCurrencyUsed: params.isCurrencyUsed
    });
  } else if (formulaType === FormulaType.TradedFunds) {
    return buildTradedFundsFormula({
      selectedDataPoint: params.selectedDataPoint,
      indexIdentification: params.indexIdentification,
      date: params.date,
      currency: params.currency
    });
  } else if (formulaType === FormulaType.CapitalizationActuals) {
    return buildCapitalizationActualsFormula({
      companyIdentification: params.companyIdentification,
      dataType: params.dataType,
      dataSource: params.dataSource,
      date: params.date,
      dataScale: params.dataScale,
      isScaleUsed: params.isScaleUsed,
      isCurrencyUsed: params.isCurrencyUsed,
      currency: params.currency,
    });
  } else if (formulaType === FormulaType.CustomBuiltIndex) {
    if (!params.date) {
      return '';
    }
    return buildCustomBuiltIndexFormula({
      inputRange: params.inputRange || "",
      date: params.date,
    });
  } else if (formulaType === FormulaType.DerivedMarketData) {
    return buildDerivedMarketDataFormula({
      selectedDataPoint: params.selectedDataPoint,
      companyIdentification: params.companyIdentification,
      date: params.date,
      currency: params.currency,
    });
  }

  return '';
}

